import { parseIntegerValue } from 'src/utils/parseIntegervalue.ts';
import { AttributeValueWithCount } from 'src/generated/operations.ts';
import { Comparator, ComparatorItem } from 'src/common/types/Comparator.ts';

type ComparatorProps = {
    sortOrder: 'asc' | 'desc';
    sortAsNumeric: boolean;
    sortBy: 'label' | 'value' | 'count';
    locale: string;
}

function getCompareFromIntl(locale: string, options: Intl.CollatorOptions): ((x: string, y: string) => number) | null {
    try {
        const collator = new Intl.Collator(
            locale,
            options,
        );
        return collator.compare;
    } catch (e) {
        return null;
    }
}

function getItemValue(
    item: AttributeValueWithCount,
    name: 'label' | 'value' | 'count',
    sortAsNumeric: boolean,
): string {
    const { label, value, count } = item;
    if (name === 'count') {
        return (count || 0).toString();
    }
    if (name === 'label') {
        return label.toString();
    }
    return sortAsNumeric
        ? parseIntegerValue(value)
        : value.toString();
}

export function initComparator({
    sortOrder,
    sortAsNumeric,
    sortBy,
    locale,
}: ComparatorProps): Comparator {
    const numeric = sortAsNumeric || sortBy === 'count';
    const options = { numeric, ignorePunctuation: true };
    const compare = getCompareFromIntl(locale, options);
    const getItem = (item: ComparatorItem) => getItemValue(item, sortBy, sortAsNumeric);
    let comparator: Comparator = (_: ComparatorItem, __: ComparatorItem) => 0;
    if (compare) {
        comparator = sortOrder === 'asc'
            ? (
                a: ComparatorItem,
                b: ComparatorItem,
            ) => compare(getItem(a), getItem(b))
            : (
                a: ComparatorItem,
                b: ComparatorItem,
            ) => compare(getItem(b), getItem(a));
    } else {
        comparator = sortOrder === 'asc'
            ? (
                a: ComparatorItem,
                b: ComparatorItem,
            ) => getItem(a)
                .localeCompare(getItem(b), locale, options)
            : (
                a:ComparatorItem,
                b: ComparatorItem,
            ) => getItem(b)
                .localeCompare(getItem(a), locale, options);
    }
    return (a, b) => {
        try {
            return comparator(a, b);
        } catch (e) {
            return 0;
        }
    };
}
